import {
  FC,
  Fragment,
  SVGProps,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisV, faRocket } from '@fortawesome/free-solid-svg-icons'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ReactComponent as AdAnalysisIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Ad-Analysis.svg'
import { ReactComponent as ContentAnalysisIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Content-Analysis.svg'
import { ReactComponent as InboxAnalysisIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Inbox-Analysis.svg'
import { ReactComponent as ReviewAnalysisIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Review-Analysis.svg'
import { ReactComponent as ArrowRightNavIcon } from 'src/assets/svg/arrow-right.svg'
import { ReactComponent as ArrowLeftIcon } from 'src/assets/svg/arrow-left-s-line.svg'
import {
  InsightCategory,
  SmartInsightsAIRecommendations,
} from 'src/charts/SmartInsightsAIRecommendations'
import { CampaignHealthCard } from 'src/charts/OverviewSection/CampaignHealthCard'
import {
  formatIndustries,
  getProjectAppearance,
} from 'src/components/ProjectsWidget/projectAppearance'
import {
  AD_ANALYSIS_LIBRARY_ROUTE,
  AI_LISTENING_PROMPT_DETAILS_ROUTE,
  AI_LISTENING_ROUTE,
  CAMPAIGNS_ROUTE,
  CONTENT_ANALYSIS_ROUTE,
  CONTENT_ANALYSIS_DETAIL_ROUTE,
  INBOX_ANALYSIS_ROUTE,
  INBOX_ANALYSIS_DETAIL_ROUTE,
  PROJECTS_ROUTE,
  REVIEW_ANALYSIS_ROUTE,
  REVIEW_ANALYSIS_CAMPAIGN_ROUTE,
} from 'src/constants/routes'
import { useGetProjectDashboardQuery } from 'src/reactQuery/useGetProjectDashboardQuery'
import { useGetProjectSmartInsightsQuery } from 'src/reactQuery/useGetProjectSmartInsightsQuery'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { GlobalQueryParams } from 'src/interfaces/globalQueryParams.interface'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { ReactComponent as AIListeningIcon } from 'src/assets/svg/ai-Listening.svg'
import { ReactComponent as SocialListeningIcon } from 'src/App/AppRouter/Layout/Sidebar/icons/Social-Listening.svg'
import styles from './projectDetails.module.scss'

const PositiveSentimentIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="2.5" />
    <path
      d="M11.3 19.1c1.2 1.6 2.9 2.4 4.7 2.4 1.9 0 3.6-.8 4.7-2.4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.5"
    />
    <circle cx="12.3" cy="12.7" fill="currentColor" r="1.5" />
    <circle cx="19.7" cy="12.7" fill="currentColor" r="1.5" />
  </svg>
)

const TopSourceIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="m16 4.7 3.4 6.9 7.6 1.1-5.5 5.4 1.3 7.6L16 22.1l-6.8 3.6 1.3-7.6-5.5-5.4 7.6-1.1L16 4.7Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
  </svg>
)

const TopDayIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <rect
      height="20"
      rx="4"
      stroke="currentColor"
      strokeWidth="2.2"
      width="22"
      x="5"
      y="7"
    />
    <path
      d="M10 4.5v5M22 4.5v5M5 12.5h22"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
    <path
      d="M10.5 17h.01M16 17h.01M21.5 17h.01M10.5 22h.01M16 22h.01M21.5 22h.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.6"
    />
  </svg>
)

const TopKeywordIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M6.5 10.2 2.8 14l3.7 3.8M25.5 10.2l3.7 3.8-3.7 3.8M19.1 7.5l-6.2 17"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.6"
    />
  </svg>
)

const TotalMentionsIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M11.7 17.4c0 4 2.9 6.9 6.9 6.9 3.8 0 6.7-2.6 6.7-6.5 0-3.8-2.8-6.4-6.7-6.4-2.2 0-4 .8-5.3 2.1-1 1.1-1.6 2.4-1.6 3.9Z"
      stroke="currentColor"
      strokeWidth="2.2"
    />
    <path
      d="M14.2 20.1c-3.1-.4-5.4-2.9-5.4-6.3 0-3.7 2.8-6.5 6.7-6.5 1.3 0 2.5.3 3.5.9"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
    <path
      d="M22.1 10.2v4.4M19.9 12.4h4.4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
  </svg>
)

const PlatformIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <circle cx="16" cy="16" r="11.3" stroke="currentColor" strokeWidth="2.2" />
    <path
      d="M4.7 16h22.6M16 4.7c2.9 3 4.6 6.7 4.6 11.3S18.9 24.3 16 27.3c-2.9-3-4.6-6.7-4.6-11.3S13.1 7.7 16 4.7Z"
      stroke="currentColor"
      strokeWidth="2.2"
    />
  </svg>
)

const CompetitorIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M16 5.2c5.2 2.9 8.6 8.4 8.6 14.3 0 2.7-2.2 4.9-4.9 4.9h-7.4a4.9 4.9 0 0 1-4.9-4.9c0-5.9 3.4-11.4 8.6-14.3Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
    <path
      d="M12.7 17.9c.9 1.3 2.1 2 3.3 2s2.4-.7 3.3-2"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
  </svg>
)

const ChatBubbleIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M8.3 8.6h15.4a3.3 3.3 0 0 1 3.3 3.3v8a3.3 3.3 0 0 1-3.3 3.3h-8.8l-5.5 4v-4H8.3A3.3 3.3 0 0 1 5 19.9v-8a3.3 3.3 0 0 1 3.3-3.3Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
    <path
      d="M11.3 14h9.4M11.3 18.4h6.4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
  </svg>
)

const TopicIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M18.3 5.1c4.8 1.7 7.8 5.6 7.8 10.3 0 5.4-4 9.1-8.7 9.1-1 0-2-.2-3-.5-1.4-.5-2.4.4-2.4 1.8v1.4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
    <path
      d="M13 27.2a2.2 2.2 0 1 0 4.4 0 2.2 2.2 0 0 0-4.4 0ZM13.7 21.5c-3.8-1.3-6.3-4.7-6.3-9 0-2.2.7-4.2 2.1-5.9"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
  </svg>
)

const PostsIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="m16 5.1 9.1 7-9.1 7-9.1-7 9.1-7ZM6.9 19.9l9.1 7 9.1-7"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
  </svg>
)

const CommentsIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M16 6.1c6 0 10.8 4 10.8 9 0 5-4.8 9-10.8 9-1.1 0-2.2-.1-3.2-.4l-4.8 1.9.7-4c-1.7-1.5-2.8-3.8-2.8-6.5 0-5 4.9-9 11.1-9Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
    <path
      d="M11.8 15.2h.1M16 15.2h.1M20.2 15.2h.1"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="3"
    />
  </svg>
)

const PhraseIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M7.4 7.6h17.2a3 3 0 0 1 3 3v9.7a3 3 0 0 1-3 3H14.4l-5.2 3.8v-3.8h-1.8a3 3 0 0 1-3-3v-9.7a3 3 0 0 1 3-3Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
    <path
      d="M11 13.1h10M11 17.3h6.8"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
  </svg>
)

const ReviewsIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="m16 5.1 3.2 6.5 7.1 1-5.1 5.1 1.2 7.2-6.4-3.4-6.4 3.4 1.2-7.2-5.1-5.1 7.1-1L16 5.1Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
  </svg>
)

const ComplaintIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M16 5.8 27.8 26H4.2L16 5.8Z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
    <path
      d="M16 12.2v6.2M16 22.1h.1"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.3"
    />
  </svg>
)

const PraiseIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="m17.6 5.4 2 5.5 5.5 2-5.5 2-2 5.5-2-5.5-5.5-2 5.5-2 2-5.5ZM24.1 18.2l1.2 3.2 3.2 1.2-3.2 1.2-1.2 3.2-1.2-3.2-3.2-1.2 3.2-1.2 1.2-3.2Z"
      fill="currentColor"
    />
  </svg>
)

const SpendIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <rect
      height="16"
      rx="4"
      stroke="currentColor"
      strokeWidth="2.2"
      width="22"
      x="5"
      y="8"
    />
    <path
      d="M21.4 14h5.6v4h-5.6a2 2 0 1 1 0-4Z"
      stroke="currentColor"
      strokeWidth="2.2"
    />
    <path
      d="M9.7 12.1h4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
  </svg>
)

const FormatIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 32 32" {...props}>
    <path
      d="M11.1 8.7h9.8v3a5.5 5.5 0 0 1-4.1 5.3v3.4M13.2 24.2h5.6M16 20.4v3.8M8 11.8H4.9M27.1 11.8H24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2.2"
    />
    <path
      d="M8 11.8a4.4 4.4 0 0 0 4.4 4.4h.4M24 11.8a4.4 4.4 0 0 1-4.4 4.4h-.4"
      stroke="currentColor"
      strokeWidth="2.2"
    />
  </svg>
)

const FolderIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path
      d="M3.5 7.5H9l1.7 2H20.5v9H3.5v-11Z"
      fill="currentColor"
      fillOpacity="0.84"
    />
  </svg>
)

const LockIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path
      d="M7.5 10V8.25a4.5 4.5 0 1 1 9 0V10"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
    <rect
      height="8.5"
      rx="2.4"
      stroke="currentColor"
      strokeWidth="2.2"
      width="11"
      x="6.5"
      y="10"
    />
    <path
      d="M12 13.1v2.3"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2.2"
    />
  </svg>
)

const ExternalArrowIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 20 20" {...props}>
    <path
      d="M6.2 13.8 13.8 6.2M8.4 6.2h5.4v5.4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.9"
    />
  </svg>
)

interface FeatureWidgetConfig {
  Icon: FC<SVGProps<SVGSVGElement>>
  actionRoute: string
  campaigns: FeatureWidgetCampaign[]
  disabledState?: {
    ctaLabel: string
    description: string
    route: string
    title: string
  }
  emptyState: {
    ctaLabel: string
    description: string
    title: string
  }
  iconAccent: string
  iconBackground: string
  stats: FeatureWidgetStat[]
  subtitle: string
  title: string
  widgetKey: string
}

interface FeatureWidgetCampaign {
  health?: {
    centerLabel: string
    metricLabel: string
    metricValue: number
    negativeLabel: string
    negativePerc: number
    neutralLabel: string
    neutralPerc: number
    positiveLabel: string
    positivePerc: number
    trend: number
  }
  id: string
  status: string
  subtitle: string
  title: string
}

interface FeatureWidgetStat {
  Icon: FC<SVGProps<SVGSVGElement>>
  iconClassName?: string
  label: string
  maxWidth?: number
  value?: string
}

const FEATURE_WIDGETS: Array<
  Omit<FeatureWidgetConfig, 'campaigns' | 'emptyState' | 'subtitle'>
> = [
  {
    Icon: SocialListeningIcon,
    actionRoute: CAMPAIGNS_ROUTE,
    iconAccent: '#2563EB',
    iconBackground: '#DBEAFE',
    stats: [
      {
        Icon: PositiveSentimentIcon,
        iconClassName: styles.positiveSentimentIcon,
        label: 'Positive sentiment',
        maxWidth: 180,
      },
      {
        Icon: TopSourceIcon,
        iconClassName: styles.topSourceIcon,
        label: 'Top source',
        maxWidth: 180,
      },
      {
        Icon: TopDayIcon,
        iconClassName: styles.topDayIcon,
        label: 'Top day',
        maxWidth: 180,
      },
      {
        Icon: TopKeywordIcon,
        iconClassName: styles.topKeywordIcon,
        label: 'Top keyword',
        maxWidth: 220,
      },
    ],
    title: 'Social Listening',
    widgetKey: 'social-listening',
  },
  {
    Icon: AIListeningIcon,
    actionRoute: AI_LISTENING_ROUTE,
    iconAccent: '#7C3AED',
    iconBackground: '#EDE9FE',
    stats: [
      {
        Icon: TotalMentionsIcon,
        iconClassName: styles.totalMentionsIcon,
        label: 'Total mentions',
        maxWidth: 180,
      },
      {
        Icon: PlatformIcon,
        iconClassName: styles.topPlatformIcon,
        label: 'Top platform',
        maxWidth: 200,
      },
      {
        Icon: CompetitorIcon,
        iconClassName: styles.topCompetitorIcon,
        label: 'Top competitor',
        maxWidth: 200,
      },
      {
        Icon: TopKeywordIcon,
        iconClassName: styles.topKeywordIcon,
        label: 'Top prompt',
        maxWidth: 320,
      },
    ],
    title: 'AI Listening',
    widgetKey: 'ai-listening',
  },
  {
    Icon: InboxAnalysisIcon,
    actionRoute: INBOX_ANALYSIS_ROUTE,
    iconAccent: '#0891B2',
    iconBackground: '#CFFAFE',
    stats: [
      {
        Icon: ChatBubbleIcon,
        iconClassName: styles.totalChatsIcon,
        label: 'Total messages',
        maxWidth: 180,
      },
      {
        Icon: PlatformIcon,
        iconClassName: styles.topPlatformIcon,
        label: 'Top platform',
        maxWidth: 200,
      },
      {
        Icon: TopDayIcon,
        iconClassName: styles.topDayIcon,
        label: 'Top day',
        maxWidth: 180,
      },
      {
        Icon: TopicIcon,
        iconClassName: styles.topTopicIcon,
        label: 'Top topic',
        maxWidth: 240,
      },
    ],
    title: 'Inbox Analysis',
    widgetKey: 'inbox-analysis',
  },
  {
    Icon: ContentAnalysisIcon,
    actionRoute: CONTENT_ANALYSIS_ROUTE,
    iconAccent: '#059669',
    iconBackground: '#D1FAE5',
    stats: [
      {
        Icon: PostsIcon,
        iconClassName: styles.totalPostsIcon,
        label: 'Total posts',
        maxWidth: 180,
      },
      {
        Icon: CommentsIcon,
        iconClassName: styles.totalCommentsIcon,
        label: 'Total comments',
        maxWidth: 180,
      },
      {
        Icon: PhraseIcon,
        iconClassName: styles.topPhraseIcon,
        // Narrowed from 320 to make room for Top category; a long phrase
        // still wraps to two lines before it truncates.
        label: 'Top phrase',
        maxWidth: 240,
      },
      {
        Icon: TopDayIcon,
        iconClassName: styles.topDayIcon,
        label: 'Top day',
        maxWidth: 180,
      },
      {
        // "Complaints (6)": the comment category with the most comments
        // across the project's analyses.
        Icon: TopicIcon,
        iconClassName: styles.topTopicIcon,
        label: 'Top category',
        maxWidth: 200,
      },
    ],
    title: 'Content Analysis',
    widgetKey: 'content-analysis',
  },
  {
    Icon: ReviewAnalysisIcon,
    actionRoute: REVIEW_ANALYSIS_ROUTE,
    iconAccent: '#D97706',
    iconBackground: '#FEF3C7',
    stats: [
      {
        Icon: ReviewsIcon,
        iconClassName: styles.totalReviewsIcon,
        label: 'Total reviews',
        maxWidth: 180,
      },
      {
        Icon: PlatformIcon,
        iconClassName: styles.topPlatformIcon,
        label: 'Top platform',
        maxWidth: 200,
      },
      {
        Icon: ComplaintIcon,
        iconClassName: styles.topComplaintIcon,
        label: 'Top complaint',
        maxWidth: 320,
      },
      {
        Icon: PraiseIcon,
        iconClassName: styles.topPraiseIcon,
        label: 'Top praise',
        maxWidth: 320,
      },
    ],
    title: 'Review Analysis',
    widgetKey: 'review-analysis',
  },
  {
    Icon: AdAnalysisIcon,
    actionRoute: AD_ANALYSIS_LIBRARY_ROUTE,
    iconAccent: '#E11D48',
    iconBackground: '#FFE4E6',
    stats: [
      {
        Icon: SpendIcon,
        iconClassName: styles.totalSpendIcon,
        label: 'Total spend',
        maxWidth: 180,
      },
      {
        Icon: PlatformIcon,
        iconClassName: styles.topPlatformIcon,
        label: 'Top platform',
        maxWidth: 200,
      },
      {
        Icon: TopDayIcon,
        iconClassName: styles.topDayIcon,
        label: 'Top day',
        maxWidth: 180,
      },
      {
        Icon: FormatIcon,
        iconClassName: styles.topFormatIcon,
        label: 'Top content format',
        maxWidth: 320,
      },
    ],
    title: 'Ad Analysis',
    widgetKey: 'ad-analysis',
  },
]

const WIDGET_BY_TITLE = Object.fromEntries(
  FEATURE_WIDGETS.map((widget) => [widget.title, widget])
)

const CampaignNoDataCard: FC<{ title: string }> = ({ title }) => {
  return (
    <article className={styles.noDataCampaignCard}>
      <div className={styles.noDataCampaignHeader}>
        <TruncatedTooltip className={styles.noDataCampaignTitle} text={title} />

        <button
          aria-label={`${title} actions`}
          className={styles.noDataCampaignMenuButton}
          type="button"
        >
          <FontAwesomeIcon
            className={styles.noDataCampaignMenuIcon}
            icon={faEllipsisV}
          />
        </button>
      </div>

      <div className={styles.noDataCampaignBody}>
        <div className={styles.noDataCampaignContent}>
          <span className={styles.noDataCampaignIconBox}>
            <FontAwesomeIcon
              className={styles.noDataCampaignIcon}
              icon={faRocket}
            />
          </span>

          <div className={styles.noDataCampaignCopy}>
            <h4 className={styles.noDataCampaignHeadline}>No data yet</h4>
            <p className={styles.noDataCampaignDescription}>
              Results will appear here once the
              {'\n'}
              first data comes in.
            </p>
          </div>
        </div>
      </div>
    </article>
  )
}

const getCampaignDetailsPath = (widgetKey: string, campaignId: string) => {
  switch (widgetKey) {
    case 'social-listening':
      return `${CAMPAIGNS_ROUTE}/${campaignId}?period=lifetime`
    case 'ai-listening':
      return AI_LISTENING_PROMPT_DETAILS_ROUTE.replace(':promptId', campaignId)
    case 'inbox-analysis':
      return INBOX_ANALYSIS_DETAIL_ROUTE.replace(':pageId', campaignId)
    case 'content-analysis':
      return CONTENT_ANALYSIS_DETAIL_ROUTE.replace(':analysisId', campaignId)
    case 'review-analysis':
      // Review cards are per-location with id "<campaignId>::<facilityId>";
      // details route is campaign-level, so use the campaign id portion.
      return REVIEW_ANALYSIS_CAMPAIGN_ROUTE.replace(
        ':campaignId',
        campaignId.split('::')[0]
      )
    case 'ad-analysis':
      return `${AD_ANALYSIS_LIBRARY_ROUTE}/${campaignId}`
    default:
      return null
  }
}

const CampaignCardsCarousel: FC<{
  campaigns: FeatureWidgetCampaign[]
  widgetKey: string
}> = ({ campaigns, widgetKey }) => {
  const navigate = useNavigate()
  const cardsContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(campaigns.length >= 3)
  const [scrollProgress, setScrollProgress] = useState(0)

  const showNavigation = campaigns.length >= 3

  const handleScroll = () => {
    if (cardsContainerRef.current) {
      const { clientWidth, scrollLeft, scrollWidth } = cardsContainerRef.current
      setShowLeftArrow(scrollLeft > 0)
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10)

      const maxScroll = scrollWidth - clientWidth
      setScrollProgress(maxScroll > 0 ? scrollLeft / maxScroll : 0)
    }
  }

  const scrollByCard = (direction: 'left' | 'right') => {
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollBy({
        behavior: 'smooth',
        left: direction === 'right' ? 404 : -404,
      })
    }
  }

  useEffect(() => {
    setShowLeftArrow(false)
    setShowRightArrow(campaigns.length >= 3)
    setScrollProgress(0)
  }, [campaigns.length])

  return (
    <>
      <div
        className={`${styles.cardsWrapper} ${
          showLeftArrow && showNavigation
            ? styles.cardsWrapperWithLeftGradient
            : ''
        } ${!showRightArrow && showNavigation ? styles.cardsWrapperNoRightGradient : ''}`}
      >
        {showLeftArrow && showNavigation && (
          <button
            className={`${styles.navButton} ${styles.navButtonLeft}`}
            onClick={() => scrollByCard('left')}
            type="button"
          >
            <ArrowRightNavIcon
              className={`${styles.navArrow} ${styles.navArrowLeft}`}
            />
          </button>
        )}

        <div
          className={styles.cardsContainer}
          onScroll={handleScroll}
          ref={cardsContainerRef}
        >
          {campaigns.map((campaign) => {
            if (!campaign.health) {
              return (
                <CampaignNoDataCard
                  key={`${widgetKey}-${campaign.id}`}
                  title={campaign.title}
                />
              )
            }

            const health = campaign.health
            if (!health) return null

            return (
              <CampaignHealthCard
                centerLabel={health.centerLabel}
                id={campaign.id}
                key={`${widgetKey}-${campaign.id}`}
                mentions={health.metricValue}
                metricLabel={health.metricLabel}
                name={campaign.title}
                negativeLabel={health.negativeLabel}
                negativePerc={health.negativePerc}
                neutralLabel={health.neutralLabel}
                neutralPerc={health.neutralPerc}
                onViewDetails={() => {
                  const detailsPath = getCampaignDetailsPath(
                    widgetKey,
                    campaign.id
                  )
                  if (detailsPath) {
                    navigate(detailsPath)
                  }
                }}
                positiveLabel={health.positiveLabel}
                positivePerc={health.positivePerc}
                trend={health.trend}
              />
            )
          })}
        </div>

        {showRightArrow && showNavigation && (
          <button
            className={styles.navButton}
            onClick={() => scrollByCard('right')}
            type="button"
          >
            <ArrowRightNavIcon className={styles.navArrow} />
          </button>
        )}
      </div>

      {showNavigation && (
        <div className={styles.scrollbarTrack}>
          <div
            className={styles.scrollbarThumb}
            style={{ left: `${scrollProgress * (100 - 43)}%` }}
          />
        </div>
      )}
    </>
  )
}

const FeatureWidget: FC<FeatureWidgetConfig> = ({
  actionRoute,
  campaigns,
  disabledState,
  emptyState,
  iconAccent,
  iconBackground,
  Icon,
  stats,
  subtitle,
  title,
  widgetKey,
}) => {
  const navigate = useNavigate()

  return (
    <section className={styles.socialListeningWidget}>
      <header className={styles.socialListeningHeader}>
        <div className={styles.socialListeningHeaderContent}>
          <span
            className={styles.socialListeningIconContainer}
            style={{ background: iconBackground }}
          >
            <Icon
              className={styles.socialListeningIcon}
              style={{ color: iconAccent }}
            />
          </span>

          <div className={styles.socialListeningCopy}>
            <h2 className={styles.socialListeningTitle}>{title}</h2>
            <p className={styles.socialListeningSubtitle}>{subtitle}</p>
          </div>
        </div>

        <button
          className={styles.addNewButton}
          onClick={() => navigate(actionRoute)}
          type="button"
        >
          <span className={styles.addNewPlus}>+</span>
          <span>Add New Campaign</span>
        </button>
      </header>

      <div className={styles.socialListeningStats}>
        {stats.map((stat, index) => (
          <Fragment key={`${title}-${stat.label}`}>
            <article className={styles.socialListeningStat}>
              <stat.Icon
                className={`${styles.socialListeningStatIcon} ${stat.iconClassName || ''}`}
              />
              <div
                className={styles.socialListeningStatCopy}
                style={
                  stat.maxWidth ? { maxWidth: `${stat.maxWidth}px` } : undefined
                }
              >
                <p className={styles.socialListeningStatLabel}>{stat.label}</p>
                <TruncatedTooltip
                  className={styles.socialListeningStatValue}
                  maxLines={2}
                  text={stat.value || '-'}
                />
              </div>
            </article>

            {index < stats.length - 1 && (
              <span className={styles.socialListeningStatDivider} />
            )}
          </Fragment>
        ))}
      </div>

      <section className={styles.socialListeningCampaigns}>
        <h3 className={styles.socialListeningCampaignsTitle}>Campaigns</h3>

        {disabledState ? (
          <div className={styles.socialListeningDisabledState}>
            <span className={styles.socialListeningDisabledIconContainer}>
              <LockIcon className={styles.socialListeningDisabledIcon} />
            </span>

            <div className={styles.socialListeningEmptyCopy}>
              <h4 className={styles.socialListeningEmptyTitle}>
                {disabledState.title}
              </h4>
              <p className={styles.socialListeningEmptyDescription}>
                {disabledState.description}
              </p>
            </div>

            <button
              className={`${styles.emptyStateButton} ${styles.disabledStateButton}`}
              onClick={() => navigate(disabledState.route)}
              type="button"
            >
              <span>{disabledState.ctaLabel}</span>
              <ExternalArrowIcon className={styles.disabledStateButtonIcon} />
            </button>
          </div>
        ) : campaigns.length === 0 ? (
          <div className={styles.socialListeningEmptyState}>
            <span className={styles.socialListeningEmptyIconContainer}>
              <FolderIcon className={styles.socialListeningEmptyIcon} />
            </span>

            <div className={styles.socialListeningEmptyCopy}>
              <h4 className={styles.socialListeningEmptyTitle}>
                {emptyState.title}
              </h4>
              <p className={styles.socialListeningEmptyDescription}>
                {emptyState.description}
              </p>
            </div>

            <button
              className={styles.emptyStateButton}
              onClick={() => navigate(actionRoute)}
              type="button"
            >
              <span className={styles.emptyStateButtonPlus}>+</span>
              <span>Add campaign</span>
            </button>
          </div>
        ) : (
          <CampaignCardsCarousel campaigns={campaigns} widgetKey={widgetKey} />
        )}
      </section>
    </section>
  )
}

export const ProjectDetails: FC = () => {
  const { projectId } = useParams<{
    projectId: string
  }>()
  const location = useLocation()
  const navigate = useNavigate()
  const sectionRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    sectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [])

  const { queryParams } = useQueryParams<GlobalQueryParams>()
  const dateRange = getDateRangeByQueryParams(queryParams)

  const { data: dashboardData } = useGetProjectDashboardQuery({
    end: dateRange?.end,
    id: projectId,
    start: dateRange?.start,
  })
  const { data: rawSmartInsights, isLoading: isSmartInsightsLoading } =
    // Same window as the dashboard above — without it the insights were
    // unfiltered and their percentages divided by lifetime totals.
    useGetProjectSmartInsightsQuery({
      fromDate: dateRange?.start,
      id: projectId,
      toDate: dateRange?.end,
    })
  const smartInsightsData = rawSmartInsights
    ? {
        hasInsights: rawSmartInsights.hasInsights,
        insights: rawSmartInsights.insights?.map((insight) => ({
          ...insight,
          category: insight.category as InsightCategory,
        })),
      }
    : undefined
  const { data: userFeatures } = useGetUserFeaturesQuery()

  const project = dashboardData?.data?.project
  const inboxEnabled = userFeatures?.data?.inboxAnalysisEnabled ?? true
  const contentEnabled = userFeatures?.data?.contentAnalysisEnabled ?? true
  const reviewEnabled = userFeatures?.data?.reviewAnalysisEnabled ?? true
  const adsLibraryEnabled = userFeatures?.data?.adsLibraryEnabled ?? true
  const adsPerformanceEnabled =
    userFeatures?.data?.adsPerformanceEnabled ?? true
  const adsEnabled = adsLibraryEnabled || adsPerformanceEnabled
  const dashboardWidgets = dashboardData?.data?.widgets || []

  const projectAppearance = useMemo(
    () => getProjectAppearance(project?.industries || []),
    [project?.industries]
  )
  const widgets = useMemo(
    () =>
      dashboardWidgets
        .map((widget) => {
          const config = WIDGET_BY_TITLE[widget.title]
          if (!config) return null

          const isFeatureDisabled = (() => {
            switch (widget.key) {
              case 'inbox-analysis':
                return !inboxEnabled
              case 'content-analysis':
                return !contentEnabled
              case 'review-analysis':
                return !reviewEnabled
              case 'ad-analysis':
                return !adsEnabled
              default:
                return false
            }
          })()

          const disabledState = (() => {
            switch (widget.key) {
              case 'social-listening':
                return {
                  ctaLabel: 'Go to Social Listening',
                  description:
                    'To track brand mentions, sentiment, and keywords, turn on Social Listening from its settings page.',
                  route: CAMPAIGNS_ROUTE,
                  title: 'Social Listening is not enabled',
                }
              case 'ai-listening':
                return {
                  ctaLabel: 'Go to AI Listening',
                  description:
                    'To monitor brand visibility across AI answers, turn on AI Listening from its settings page.',
                  route: AI_LISTENING_ROUTE,
                  title: 'AI Listening is not enabled',
                }
              case 'inbox-analysis':
                return {
                  ctaLabel: 'Go to Inbox Analysis',
                  description:
                    'To analyze incoming messages and customer conversations, turn on Inbox Analysis from its settings page.',
                  route: INBOX_ANALYSIS_ROUTE,
                  title: 'Inbox Analysis is not enabled',
                }
              case 'content-analysis':
                return {
                  ctaLabel: 'Go to Content Analysis',
                  description:
                    'To analyze posts, comments, and engagement, turn on Content Analysis from its settings page.',
                  route: CONTENT_ANALYSIS_ROUTE,
                  title: 'Content Analysis is not enabled',
                }
              case 'review-analysis':
                return {
                  ctaLabel: 'Go to Review Analysis',
                  description:
                    'To analyze reviews and brand reputation, turn on Review Analysis from its settings page.',
                  route: REVIEW_ANALYSIS_ROUTE,
                  title: 'Review Analysis is not enabled',
                }
              case 'ad-analysis':
                return {
                  ctaLabel: 'Go to Ad Analysis',
                  description:
                    'To analyze ads and competitor pages, turn on Ad Analysis from its settings page.',
                  route: AD_ANALYSIS_LIBRARY_ROUTE,
                  title: 'Ad Analysis is not enabled',
                }
              default:
                return undefined
            }
          })()

          const summaryText = widget.summary?.text ?? widget.subtitle

          return {
            ...config,
            campaigns: widget.campaigns,
            disabledState: isFeatureDisabled ? disabledState : undefined,
            emptyState: widget.emptyState,
            stats: config.stats.map((stat) => ({
              ...stat,
              value: isFeatureDisabled
                ? '-'
                : widget.stats.find(
                    (candidate) => candidate.label === stat.label
                  )?.value || '-',
            })),
            subtitle: summaryText,
            widgetKey: widget.key,
          }
        })
        .filter(Boolean) as FeatureWidgetConfig[],
    [adsEnabled, contentEnabled, dashboardWidgets, inboxEnabled, reviewEnabled]
  )

  const handleClose = () => {
    navigate(`${PROJECTS_ROUTE}${location.search}`)
  }

  return (
    <section className={styles.projectDetailsPage} ref={sectionRef}>
      <header className={styles.header}>
        <button
          className={styles.backButton}
          onClick={handleClose}
          type="button"
        >
          <ArrowLeftIcon className={styles.backIcon} />
        </button>

        <div className={styles.projectSummary}>
          <span
            className={styles.iconContainer}
            style={{
              background: projectAppearance.background,
              color: projectAppearance.accent,
            }}
          >
            <projectAppearance.Icon className={styles.projectIcon} />
          </span>

          <div className={styles.projectCopy}>
            <h1 className={styles.projectTitle}>
              {project?.name || 'Project'}
            </h1>
            <p className={styles.projectSubtitle}>
              {formatIndustries(project?.industries || []) || 'Project details'}
            </p>
          </div>
        </div>
      </header>

      <SmartInsightsAIRecommendations
        className={styles.smartInsights}
        data={smartInsightsData}
        hideProjectFilter
        homePageVariant
        isLoading={isSmartInsightsLoading}
        showScopeTabs
      />

      {widgets.map((widget) => (
        <FeatureWidget
          Icon={widget.Icon}
          actionRoute={widget.actionRoute}
          campaigns={widget.campaigns}
          disabledState={widget.disabledState}
          emptyState={widget.emptyState}
          iconAccent={widget.iconAccent}
          iconBackground={widget.iconBackground}
          key={widget.title}
          stats={widget.stats}
          subtitle={widget.subtitle}
          title={widget.title}
          widgetKey={widget.widgetKey}
        />
      ))}
    </section>
  )
}
